/**
 * Drawer zum Anlegen und Bearbeiten eines Tagesplan-Eintrags (CRM-Tab und
 * öffentlicher Kundenlink). Der Entwurf lebt beim Elternteil; ein neuer
 * Eintrag gelangt erst mit „Speichern" (und ausgefüllter Beschreibung) in den
 * Plan, Abbrechen verwirft ihn.
 */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer } from '../../../../components/ui';
import { TimeInput24 } from '../../../../components/DateTimeInput';
import { getThemeColors } from '../../../../lib/theme';
import {
  NOTE_MAX_LEN,
  PB_ACTIVITIES,
  activityLabel,
  hasFixedDuration,
} from './scheduleUtils';

/** Entwurf im Drawer; `_key === null` = neuer Eintrag. */
export interface EntryDraft {
  _key: string | null;
  care_type: string | null;
  start_time: string;
  end_time: string;
  note: string | null;
  /** Beschreibung des Eintrags beim Öffnen (für „bestehend ohne Text bleibt speicherbar"). */
  originalNote: string | null;
}

interface Props {
  draft: EntryDraft | null;
  dark: boolean;
  error: string | null;
  /** Beschreibung ist Pflicht (neuer Eintrag, außer Pause). */
  noteRequired: boolean;
  onActivity: (careType: string) => void;
  onStart: (start: string) => void;
  onPatch: (patch: Partial<EntryDraft>) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export function ScheduleEntryDrawer({
  draft, dark, error, noteRequired, onActivity, onStart, onPatch, onSave, onDelete, onClose,
}: Props) {
  const { t: tr } = useTranslation();
  const tc = getThemeColors(dark);
  const inputStyle: React.CSSProperties = {
    fontSize: 14, padding: '10px 12px', border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif', outline: 'none',
    boxSizing: 'border-box', width: '100%',
  };
  const fixed = draft ? hasFixedDuration(draft.care_type) : false;
  const noteLen = (draft?.note ?? '').length;
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const isOpen = draft !== null;

  // Eingegebener Text geht beim Schließen über Hintergrund, Escape oder × nicht
  // stillschweigend verloren (Beschreibung wird oft lang getippt, v. a. am Handy).
  const dirty = !!draft && (draft.note ?? '') !== (draft.originalNote ?? '');
  const guardedClose = () => {
    if (dirty && !window.confirm(tr('public.schedule.drawer.discard_confirm'))) return;
    onClose();
  };
  const guardedCloseRef = useRef(guardedClose);
  guardedCloseRef.current = guardedClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    noteRef.current?.focus();   // Pflichtfeld sofort bereit
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') guardedCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  return (
    <Drawer open={draft !== null} onClose={guardedClose} width={460} dark={dark}>
      {draft && (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${tc.borderSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: tc.heading }}>
              {draft._key ? tr('public.schedule.drawer.edit_title') : tr('public.schedule.activities.add_button')}
            </h2>
            <button type="button" aria-label={tr('common.action.close')} onClick={guardedClose}
              style={{ background: 'transparent', border: 'none', fontSize: 22, lineHeight: 1, color: tc.muted, cursor: 'pointer' }}>×</button>
          </div>

          <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: tc.muted }}>{tr('pflege.schedule.activity')}</span>
              <select aria-label={tr('pflege.schedule.activity')} value={draft.care_type ?? ''} onChange={e => onActivity(e.target.value)} style={inputStyle}>
                <option value="">{tr('public.schedule.drawer.activity_placeholder')}</option>
                {PB_ACTIVITIES.map(a => <option key={a} value={a}>{activityLabel(a, tr)}</option>)}
              </select>
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: tc.muted }}>{tr('pflege.schedule.from')}</span>
                <TimeInput24 ariaLabel={tr('pflege.schedule.from')} value={draft.start_time} onChange={v => onStart(v)} style={inputStyle} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: tc.muted }}>{tr('pflege.schedule.to')}{fixed && ` · ${tr('pflege.schedule.autoEnd')}`}</span>
                <TimeInput24 ariaLabel={tr('pflege.schedule.to')} value={draft.end_time} disabled={fixed}
                  onChange={v => onPatch({ end_time: v })}
                  style={{ ...inputStyle, opacity: fixed ? 0.6 : 1, cursor: fixed ? 'not-allowed' : 'auto' }} />
              </label>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: tc.muted }}>
                {tr('public.schedule.drawer.note_label')}{noteRequired && <span aria-hidden style={{ color: '#b42318' }}> *</span>}
              </span>
              <textarea
                ref={noteRef}
                aria-label={tr('pflege.schedule.note')}
                aria-required={noteRequired || undefined}
                value={draft.note ?? ''}
                rows={4}
                maxLength={NOTE_MAX_LEN}
                placeholder={tr('public.schedule.drawer.note_placeholder')}
                onChange={e => onPatch({ note: e.target.value || null })}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.4 }}
              />
              <span style={{ alignSelf: 'flex-end', fontSize: 11, color: tc.muted, fontVariantNumeric: 'tabular-nums' }}>
                {noteLen} / {NOTE_MAX_LEN}
              </span>
            </label>

            {error && (
              <div role="alert" style={{ padding: '9px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 12.5, color: '#b42318' }}>
                {error}
              </div>
            )}
          </div>

          <div style={{ padding: '14px 20px', borderTop: `1px solid ${tc.borderSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div>
              {draft._key && (
                <button type="button" onClick={onDelete}
                  style={{ fontSize: 12.5, padding: '8px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fecdca', borderRadius: 7, color: '#b42318', cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
                  {tr('mfa.settings.action.remove')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button dark={dark} variant="secondary" size="sm" onClick={onClose}>{tr('common.action.cancel')}</Button>
              <Button dark={dark} variant="primary" size="sm" onClick={onSave}>{tr('common.action.save')}</Button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}

/** Übersetzte Meldung zu einem Entwurfsfehler (Drawer). */
export function entryDraftErrorMessage(
  code: 'NO_ACTIVITY' | 'INVALID_TIME' | 'OVERLAP' | 'NOTE_REQUIRED' | 'NOTE_TOO_LONG',
  tr: (key: string, opts?: Record<string, unknown>) => string,
): string {
  switch (code) {
    case 'NO_ACTIVITY':   return tr('public.schedule.error.choose_activity');
    case 'INVALID_TIME':  return tr('pflege.schedule.endBeforeStartError');
    case 'OVERLAP':       return tr('public.schedule.error.drawer_overlap');
    case 'NOTE_REQUIRED': return tr('public.schedule.error.note_required');
    case 'NOTE_TOO_LONG': return tr('public.schedule.error.note_too_long', { max: NOTE_MAX_LEN });
  }
}
